import { Component, OnInit, ChangeDetectionStrategy, Input, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { MeteringFilter, UserSettings, MeteringPointStub, MeteringPoint, MeteringSubFilter } from '@model';
import { MeteringFilterConfigFormComponent } from '@core/components/metering-filter-config-form/metering-filter-config-form.component';
import { SxDialogService } from '../../../services/sx-dialog.service';
import { MeteringType, MeteringFilterType } from '@enums';
import * as fromAccountStore from '@appStore/account';
import * as fromAnalyzeStore from '@appStore/analyze';
import * as fromCustomerStore from '@appStore/customers';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatExpansionPanel } from '@angular/material/expansion';
import { SubSink } from 'subsink';

@Component({
    selector: 'app-metering-filter',
    templateUrl: './metering-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MeteringFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  meteringType: MeteringType;
  @Input()
  userSettings: UserSettings;
  @Input()
  subfilterEnabled = false;
  @Input()
  meteringPoints: MeteringPoint[];
  @Input()
  filterArea: string;
  @Input()
  coldMeteringPointsCount: number;
  @Input()
  hotMeteringPointsCount: number;

  meteringPointsCount = 0;
  meteringPointsCountLabel = '';
  showMeteringFilter = false;

  singleMeteringPointName = '';

  @ViewChild('subfilterPanel')
  subfilterPanel: MatExpansionPanel;

  subfilterOpenState = false;

  panelOpenState = false;
  filterUpdateWatch: Subject<MeteringSubFilter>;
  filterSettings: MeteringFilter;
  filterSettingsTypeEnum = MeteringFilterType;

  private subs = new SubSink();

  constructor(
    private sxDialogService: SxDialogService,
    private accountStore: Store<fromAccountStore.AccountState>,
    private customerStore: Store<fromCustomerStore.CustomersState>,
    private analyzeStore: Store<fromAnalyzeStore.AnalyzeState>
  ) {}

  ngOnInit() {
    // Filter out metering points that belongs to whitelabel customers
    this.subs.sink = this.customerStore.select(fromCustomerStore.getCustomers).subscribe(customers => {
    
      // Filter out mpIds where customer is white label
      if (customers) {
        this.meteringPoints = this.meteringPoints.filter(mp => {
          let customer = customers.find(c => c.id === mp.customerId);
          return customer ? !customer.isWhiteLabel : false;
        });
      } 

      this.initFromSettings();

      this.filterUpdateWatch = new Subject<MeteringSubFilter>();
      this.subs.sink = this.filterUpdateWatch
        .pipe(debounceTime(750), distinctUntilChanged())
        .subscribe((subfilter) => this.accountStore.dispatch(new fromAccountStore.SetSubFilter(subfilter)));

        // Visa alltid filter i Analys och Fakturor för både FJV och FJK OM användaren
        // har behörighet till några anläggningar för både FJV och FJK, oavsett hur många.
        this.showMeteringFilter = ((this.coldMeteringPointsCount > 0 && this.hotMeteringPointsCount > 0) || this.meteringPointsCount > 1) ||
          (this.filterSettings && this.filterSettings.filterType === MeteringFilterType.VirtualFilter);
    });
  }

  ngOnChanges() {
    this.initFromSettings();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getMeteringFilter = (meteringType: MeteringType): MeteringFilter => {
    switch (meteringType) {
      case MeteringType.Hot:
        return this.userSettings.hotMeteringFilter;
      case MeteringType.Cold:
        return this.userSettings.coldMeteringFilter;
      case MeteringType.HotLogical:
        return this.userSettings.hotLogicalMeteringFilter;
      case MeteringType.ColdLogical:
        return this.userSettings.coldLogicalMeteringFilter;
    }
  };
  getMeteringPointCount = (meteringType: MeteringType): number => {
    if (!this.meteringPoints || this.meteringPoints.length === 0) {
      return 0;
    }

    switch (meteringType) {
      case MeteringType.Hot:
        return this.meteringPoints.filter((mp) => mp.meteringType === meteringType).length;
      case MeteringType.Cold:
        return this.meteringPoints.filter((mp) => mp.meteringType === meteringType).length;
      case MeteringType.HotLogical:
        return this.meteringPoints.filter((mp) => mp.meteringType === meteringType && mp.type === 'LOG').length;
      case MeteringType.ColdLogical:
        return this.meteringPoints.filter((mp) => mp.meteringType === meteringType && mp.type === 'LOG').length;
    }
  };

  getMeteringPointName(meteringType: MeteringType): string {
    if (!this.meteringPoints || this.meteringPoints.length === 0) {
      return '';
    }

    return this.meteringPoints.filter((mp) => mp.meteringType === meteringType)[0].name;
  }

  initFromSettings() {
    this.meteringPointsCount = this.getMeteringPointCount(this.meteringType);
    if (this.meteringPointsCount === 1) {
      this.singleMeteringPointName = this.getMeteringPointName(this.meteringType);
    }

    if (this.userSettings) {
      this.filterSettings = this.getMeteringFilter(this.meteringType);

      if (
        this.filterSettings &&
        this.filterSettings.subfilter &&
        !this.filterSettings.subfilter.excludedMeteringPoints
      ) {
        let exludedIds = this.filterSettings.subfilter.includedMeteringPoints
          ? (this.filterSettings.filterType === MeteringFilterType.Unfiltered
              ? this.meteringPoints.filter((mp) => mp.meteringType === this.meteringType).map((mp) => mp.id)
              : this.filterSettings.containsMeteringPointIds.map((mp) => mp)
            ).filter((mpid) => !this.filterSettings.subfilter.includedMeteringPoints.find((imp) => imp.id === mpid))
          : [];

        this.filterSettings = {
          ...this.filterSettings,
          subfilter: {
            ...this.filterSettings.subfilter,
            includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
              ? this.meteringPoints
                  .filter((mp) => this.filterSettings.subfilter.includedMeteringPoints.find((imp) => imp.id === mp.id))
                  .map((mp) => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true })
              : null,
            excludedMeteringPoints: this.meteringPoints
              .filter((mp) => exludedIds.find((exl) => exl === mp.id))
              .map((mp) => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: false })
          }
        };
      }
    }

    this.updateSubFilterLabel();
  }

  toggleSelected = (mps: MeteringPointStub) => {

    mps = { ...mps, selected: !mps.selected };

    // Last selected metering point in subfilter, de-selected
    if (!mps.selected && this.isLastSelected(mps)) {
      this.toggleAllInSubfilter(false);
      return false
    }

    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map((mp) => (mp.id === mps.id ? mps : mp))
          : null,
        excludedMeteringPoints: this.filterSettings.subfilter.excludedMeteringPoints
          ? this.filterSettings.subfilter.excludedMeteringPoints.map((mp) => (mp.id === mps.id ? mps : mp))
          : null
      } as MeteringSubFilter
    };
    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
  };

  isLastSelected = (mps: MeteringPointStub) => {
    let selectedExcluded = this.filterSettings.subfilter.excludedMeteringPoints
      ? this.filterSettings.subfilter.excludedMeteringPoints.filter((mp) => mp.selected)
      : [];
    let selectedIncluded = this.filterSettings.subfilter.includedMeteringPoints
      ? this.filterSettings.subfilter.includedMeteringPoints.filter((mp) => mp.selected)
      : [];
    return selectedExcluded.length + selectedIncluded.length === 1;
  };

  configureFilter = () => {
    let dialogRef = this.sxDialogService.openDialog(MeteringFilterConfigFormComponent, {
      meteringType: this.meteringType,
      filterSettings:
        this.meteringType === MeteringType.Hot
          ? this.userSettings.hotMeteringFilter
          : this.userSettings.coldMeteringFilter,
      filterArea: this.filterArea
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'save' && this.subfilterEnabled && this.subfilterPanel && this.subfilterPanel.expanded) {
        this.subfilterPanel.close();
      }
    });
  };

  updateSubFilterLabel = () => {
    if (this.filterSettings?.subfilter && this.subfilterEnabled) {
      this.meteringPointsCount = 0;
      let meteringPointsSelectedCount = 0;

      if (this.filterSettings.subfilter.includedMeteringPoints) {
        this.meteringPointsCount =
          this.meteringPointsCount + this.filterSettings.subfilter.includedMeteringPoints.length;
      }

      if (this.filterSettings.subfilter.excludedMeteringPoints) {
        this.meteringPointsCount =
          this.meteringPointsCount + this.filterSettings.subfilter.excludedMeteringPoints.length;
      }
      if (this.filterSettings.subfilter.includedMeteringPoints) {
        meteringPointsSelectedCount =
          meteringPointsSelectedCount +
          this.filterSettings.subfilter.includedMeteringPoints.filter((i) => i.selected).length;
      }

      if (this.filterSettings.subfilter.excludedMeteringPoints) {
        meteringPointsSelectedCount =
          meteringPointsSelectedCount +
          this.filterSettings.subfilter.excludedMeteringPoints.filter((i) => i.selected).length;
      }

      this.meteringPointsCountLabel = meteringPointsSelectedCount + '/' + this.meteringPointsCount;
      return;
    }

    if (!this.filterSettings || this.filterSettings.filterType === MeteringFilterType.Unfiltered) {
      this.meteringPointsCountLabel = this.meteringPointsCount + '';
      return;
    }

    if (!this.filterSettings || this.filterSettings.filterType === MeteringFilterType.VirtualFilter) {
      this.meteringPointsCountLabel = this.filterSettings.containsMeteringPointIds.length + '';
      return;
    }

    if (this.filterSettings.containsMeteringPointIds && this.filterSettings.containsMeteringPointIds.length === 1) {
      this.meteringPointsCountLabel = '';
      return;
    }

    this.meteringPointsCountLabel = this.filterSettings.subtitle || '';
  };

  resetSubfilter = () => {
    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        excludedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.excludedMeteringPoints.map(
              (mp) => <MeteringPointStub>{ ...mp, selected: false }
            )
          : this.filterSettings.subfilter.excludedMeteringPoints.map(
              (mp) => <MeteringPointStub>{ ...mp, selected: true }
            ),
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map(
              (mp) => <MeteringPointStub>{ ...mp, selected: true }
            )
          : this.filterSettings.subfilter.includedMeteringPoints
      }
    };

    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    this.updateSubFilterLabel();
  };

  toggleAllInSubfilter = (state: boolean) => {
    this.filterSettings = {
      ...this.filterSettings,
      subfilter: {
        ...this.filterSettings.subfilter,
        excludedMeteringPoints:
          !this.filterSettings.subfilter.includedMeteringPoints || this.panelOpenState === true
            ? this.filterSettings.subfilter.excludedMeteringPoints.map(
                (mp) => <MeteringPointStub>{ ...mp, selected: state }
              )
            : this.filterSettings.subfilter.excludedMeteringPoints,
        includedMeteringPoints: this.filterSettings.subfilter.includedMeteringPoints
          ? this.filterSettings.subfilter.includedMeteringPoints.map(
              (mp) => <MeteringPointStub>{ ...mp, selected: state }
            )
          : this.filterSettings.subfilter.includedMeteringPoints
      }
    };

    this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    this.updateSubFilterLabel();

    if (!state) {
      // Återställer Graf och Tabell när alla anläggningar avmarkeras
      this.analyzeStore.dispatch(new fromAnalyzeStore.AnalyzeResetGraphValues());
    }
  };

  triggerSubFilterUpdate = (subfilter: MeteringSubFilter) => {
    this.filterUpdateWatch.next(subfilter);
  };

  toggleSubFilter = () => {
    if (!this.filterSettings.subfilter) {
      this.filterSettings = {
        ...this.filterSettings,
        subfilter: <MeteringSubFilter>{
          meteringType: this.filterSettings.meteringType,
          excludedMeteringPoints: (this.filterSettings.filterType !== MeteringFilterType.Unfiltered
            ? this.meteringPoints.filter((mp) =>
                this.filterSettings.containsMeteringPointIds.find((mpid) => mpid === mp.id)
              )
            : this.meteringPoints.filter((mp) => mp.meteringType === this.meteringType)
          ).map((mp) => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true })
        }
      };
      this.triggerSubFilterUpdate(this.filterSettings.subfilter);
    }
    if (this.subfilterPanel) {
      this.subfilterPanel.toggle();
      this.subfilterOpenState = this.subfilterPanel.expanded;
    }
  };
}
